'use client';

import { type ChangeEvent, memo, useCallback } from 'react';

import { LockKeyholeIcon, LockKeyholeOpenIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type VersionReference, VersionReferenceRow } from '~/components/asset-detail/VersionReferenceRow';
import { IconButton } from '~/components/IconButton';
import { LabeledInput } from '~/components/LabeledInput';
import { SectionLabel } from '~/components/SectionLabel';

export type { VersionReference } from '~/components/asset-detail/VersionReferenceRow';

export type ImageOptionsSectionProps = {
  showVersionReference: boolean;
  versionReference: VersionReference;
  onVersionReferenceChange: (value: VersionReference) => void;
  width: number;
  height: number;
  isAspectRatioLocked: boolean;
  onToggleAspectRatioLock: () => void;
  onWidthChange: (width: number) => void;
  onHeightChange: (height: number) => void;
  /** Whether the Dynamic option is clickable */
  isDynamicEnabled?: boolean;
  /** Disable W/H inputs and lock button when dynamic is selected */
  isResizeDisabled?: boolean;
  /** Section header text. Defaults to "Image Options". */
  sectionLabel?: string;
};

export const ImageOptionsSection = memo(
  ({
    showVersionReference,
    versionReference,
    onVersionReferenceChange,
    width,
    height,
    isAspectRatioLocked,
    onToggleAspectRatioLock,
    onWidthChange,
    onHeightChange,
    isDynamicEnabled = false,
    isResizeDisabled = false,
    sectionLabel = 'Image Options',
  }: ImageOptionsSectionProps) => {
    const handleWidthChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newWidth = Number(event.target.value);
        onWidthChange(newWidth);
      },
      [onWidthChange],
    );

    const handleHeightChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newHeight = Number(event.target.value);
        onHeightChange(newHeight);
      },
      [onHeightChange],
    );

    const LockIcon = isAspectRatioLocked ? LockKeyholeIcon : LockKeyholeOpenIcon;

    return (
      <div className="flex flex-col gap-3">
        <SectionLabel>{sectionLabel}</SectionLabel>

        {showVersionReference && (
          <VersionReferenceRow
            isDynamicEnabled={isDynamicEnabled}
            onVersionReferenceChange={onVersionReferenceChange}
            versionReference={versionReference}
          />
        )}

        <div className={cn('flex items-center justify-between', isResizeDisabled && 'opacity-50')}>
          <span className="text-12 text-neutral-11">Resize image</span>
          <div className="flex items-center">
            <LabeledInput
              className="w-16"
              disabled={isResizeDisabled}
              hideSpinner
              label="W"
              min={0}
              onChange={handleWidthChange}
              size="small"
              type="number"
              value={width || ''}
            />
            <div className="flex w-8 items-center justify-center">
              <IconButton
                aria-label={isAspectRatioLocked ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                disabled={isResizeDisabled}
                onClick={onToggleAspectRatioLock}
                size="small"
              >
                <LockIcon size="sm" />
              </IconButton>
            </div>
            <LabeledInput
              className="w-16"
              disabled={isResizeDisabled}
              hideSpinner
              label="H"
              min={0}
              onChange={handleHeightChange}
              size="small"
              type="number"
              value={height || ''}
            />
          </div>
        </div>
      </div>
    );
  },
);

ImageOptionsSection.displayName = 'ImageOptionsSection';
